import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  WhatsappLogo,
  Bed,
  Bathtub,
  Television,
  Car,
  WifiHigh,
  Fire,
  Snowflake,
  MapPin,
  Users,
  Star,
  House,
  Basketball,
  PawPrint,
  ForkKnife,
  ArrowRight,
  X,
  Clock,
  PencilSimple,
  Lock,
  Key,
  CalendarX,
  SignIn,
  SignOut,
  Warning,
  CheckCircle,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import Background3D from "../components/Background3D";
import ReviewForm from "../components/ReviewForm";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRICE_PER_NIGHT = 1500;
const MAX_GUESTS = 7;
const WHATSAPP_NUMBER = "528333039700";

// Images with descriptions
const IMAGES = {
  // Espacios principales
  sala: {
    url: "https://customer-assets.emergentagent.com/job_estancia-tampico/artifacts/izr0ok96_WhatsApp%20Image%202026-03-22%20at%202.08.44%20PM%20%285%29.jpeg",
    alt: "Sala con sofá y aire acondicionado",
    label: "Sala"
  },
  comedor: {
    url: "https://customer-assets.emergentagent.com/job_estancia-tampico/artifacts/ob2il2vd_WhatsApp%20Image%202026-03-22%20at%202.08.44%20PM%20%283%29.jpeg",
    alt: "Comedor con mesa de madera para 6 personas",
    label: "Comedor"
  },
  television: {
    url: "https://customer-assets.emergentagent.com/job_estancia-tampico/artifacts/nk54w6ri_WhatsApp%20Image%202026-03-22%20at%202.08.44%20PM%20%287%29.jpeg",
    alt: "Televisión de pantalla plana en la sala",
    label: "TV de pantalla plana"
  },
  cocina: {
    url: "https://customer-assets.emergentagent.com/job_estancia-tampico/artifacts/kbepfurr_WhatsApp%20Image%202026-03-22%20at%202.08.44%20PM%20%286%29.jpeg",
    alt: "Cocina integral equipada con gabinetes de madera",
    label: "Cocina integral"
  },
  // Recámaras
  recamara1: {
    url: "https://customer-assets.emergentagent.com/job_estancia-tampico/artifacts/7qa001ey_WhatsApp%20Image%202026-03-22%20at%202.08.45%20PM%20%283%29.jpeg",
    alt: "Recámara principal con cama matrimonial y aire acondicionado",
    label: "Recámara principal"
  },
  recamara2: {
    url: "https://customer-assets.emergentagent.com/job_estancia-tampico/artifacts/zhto4iei_WhatsApp%20Image%202026-03-22%20at%202.08.46%20PM%20%281%29.jpeg",
    alt: "Segunda recámara con cama matrimonial y espejo",
    label: "Recámara 2"
  },
  recamaraBano: {
    url: "https://customer-assets.emergentagent.com/job_estancia-tampico/artifacts/1izwctx3_WhatsApp%20Image%202026-03-22%20at%202.08.45%20PM%20%282%29.jpeg",
    alt: "Recámara con baño privado y aire acondicionado",
    label: "Recámara con baño"
  },
  // Baños
  bano: {
    url: "https://customer-assets.emergentagent.com/job_estancia-tampico/artifacts/hxp1rq6z_WhatsApp%20Image%202026-03-22%20at%202.08.44%20PM%20%281%29.jpeg",
    alt: "Baño completo con regadera",
    label: "Baño 1"
  },
  bano2: {
    url: "https://customer-assets.emergentagent.com/job_estancia-tampico/artifacts/of2p55xa_WhatsApp%20Image%202026-03-22%20at%202.08.43%20PM%20%281%29.jpeg",
    alt: "Baño completo con regadera y lavabo",
    label: "Baño 2"
  },
  // Exteriores
  entrada: {
    url: "https://customer-assets.emergentagent.com/job_estancia-tampico/artifacts/trozw1qp_WhatsApp%20Image%202026-03-22%20at%202.08.45%20PM%20%287%29.jpeg",
    alt: "Entrada del departamento A",
    label: "Entrada"
  },
  cancha: {
    url: "https://customer-assets.emergentagent.com/job_estancia-tampico/artifacts/xaw36imj_WhatsApp%20Image%202026-03-24%20at%209.52.04%20AM.jpeg",
    alt: "Cancha de basketball del fraccionamiento",
    label: "Cancha de basketball"
  },
  beach: {
    url: "https://images.pexels.com/photos/1032650/pexels-photo-1032650.jpeg",
    alt: "Playa Miramar Tampico",
    label: "Playa Miramar"
  },
};

// Default testimonials data (shown when no dynamic reviews)
const defaultTestimonials = [
  {
    name: "Mariana López",
    time: "Hace 2 semanas",
    rating: 5,
    comment: "¡Excelente lugar! Muy limpio, cómodo y el anfitrión muy amable. Perfecto para familias.",
    avatar: "https://randomuser.me/api/portraits/women/44.jpg",
  },
  {
    name: "Carlos Hernández",
    time: "Hace 1 mes",
    rating: 4,
    comment: "La ubicación es perfecta, cerca de todo. El departamento tiene todo lo necesario. Volveremos pronto.",
    avatar: "https://randomuser.me/api/portraits/men/32.jpg",
  },
  {
    name: "Sofía Martínez",
    time: "Hace 3 semanas",
    rating: 5,
    comment: "Nos encantó la cancha de basketball, los niños se divirtieron mucho. Muy recomendado.",
    avatar: "https://randomuser.me/api/portraits/women/68.jpg",
  },
  {
    name: "Luis Ramírez",
    time: "Hace 1 mes",
    rating: 4,
    comment: "Buena relación calidad-precio. Todo limpio y organizado. El aire acondicionado funciona bien.",
    avatar: "https://randomuser.me/api/portraits/men/75.jpg",
  },
  {
    name: "Andrea Torres",
    time: "Hace 2 semanas",
    rating: 3,
    comment: "Lugar tranquilo y seguro. La cocina está equipada. Buen lugar para descansar.",
    avatar: "https://randomuser.me/api/portraits/women/90.jpg",
  },
];

// Restaurants data with images
const restaurants = [
  { 
    name: "El Lindero Universidad", 
    type: "Comida mexicana", 
    image: "https://customer-assets.emergentagent.com/job_estancia-tampico/artifacts/ft12sdbj_el%20lindero.jpg"
  },
  { 
    name: "Los Curricanes", 
    type: "Mariscos y carnes", 
    image: "https://customer-assets.emergentagent.com/job_estancia-tampico/artifacts/3x2gt0x0_curricanes.png"
  },
  { 
    name: "Toyama Sushi", 
    type: "Japonés", 
    image: "https://customer-assets.emergentagent.com/job_estancia-tampico/artifacts/arqlrxzn_sushi.jpg"
  },
  { 
    name: "Puerta 27", 
    type: "Cortes y cenas", 
    image: "https://customer-assets.emergentagent.com/job_estancia-tampico/artifacts/ygr0cn6s_puerta.png"
  },
  { 
    name: "Café Andrade", 
    type: "Cafetería", 
    image: "https://customer-assets.emergentagent.com/job_estancia-tampico/artifacts/pn1jrbyl_cafe%20androide.jpg"
  },
];

// Amenities data
const amenities = [
  { icon: Snowflake, label: "Aire acondicionado", desc: "Clima fresco" },
  { icon: WifiHigh, label: "WiFi", desc: "Alta velocidad" },
  { icon: ForkKnife, label: "Cocina equipada", desc: "Todo incluido" },
  { icon: Car, label: "Estacionamiento", desc: "Seguro" },
  { icon: House, label: "Fraccionamiento", desc: "Privado" },
  { icon: Basketball, label: "Cancha basketball", desc: "Recreación" },
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

export default function HomePage() {
  const navigate = useNavigate();
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [reviewFormOpen, setReviewFormOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);
  
  // Admin access modal state
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [adminError, setAdminError] = useState("");
  
  const ADMIN_PASSWORD = "augh809e";
  
  const handleAdminAccess = (e) => {
    e.preventDefault();
    if (adminPassword === ADMIN_PASSWORD) {
      setAdminModalOpen(false);
      setAdminPassword("");
      setAdminError("");
      navigate("/admin");
    } else {
      setAdminError("Contraseña incorrecta");
    }
  };

  // Fetch reviews from API
  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    } finally {
      setLoadingReviews(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  // Helper to format time ago
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Hoy";
    if (diffDays === 1) return "Ayer";
    if (diffDays < 7) return `Hace ${diffDays} días`;
    if (diffDays < 14) return "Hace 1 semana";
    if (diffDays < 30) return `Hace ${Math.floor(diffDays / 7)} semanas`;
    if (diffDays < 60) return "Hace 1 mes";
    return `Hace ${Math.floor(diffDays / 30)} meses`;
  };

  // Combine default testimonials with dynamic reviews
  const allTestimonials = [
    ...reviews.map(r => ({
      ...r,
      time: formatTimeAgo(r.created_at),
    })),
    ...defaultTestimonials,
  ].slice(0, 8); // Limit to 8 total

  const galleryImages = [
    { ...IMAGES.sala },
    { ...IMAGES.comedor },
    { ...IMAGES.television },
    { ...IMAGES.cocina },
    { ...IMAGES.recamara1 },
    { ...IMAGES.recamara2 },
    { ...IMAGES.bano },
    { ...IMAGES.bano2 },
    { ...IMAGES.entrada },
    { ...IMAGES.cancha },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7] relative">
      {/* 3D Interactive Background */}
      <Background3D />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <House weight="duotone" className="w-7 h-7 text-[#E05A47]" />
            <span className="font-['Plus_Jakarta_Sans'] font-bold text-lg text-stone-800">
              Tu Hogar en Tampico
            </span>
          </div>
          <Link to="/reservar">
            <Button
              className="bg-[#E05A47] hover:bg-[#C84938] text-white rounded-full px-6"
              data-testid="nav-reserve-btn"
            >
              Reservar
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen pt-20" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 py-12 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Hero Text */}
            <motion.div
              className="lg:col-span-5 space-y-6"
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
            >
              <span className="inline-block uppercase tracking-[0.2em] text-xs font-semibold text-stone-500 bg-[#F5F0E6] px-4 py-2 rounded-full">
                Tampico, México
              </span>
              <h1 className="font-['Plus_Jakarta_Sans'] text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 leading-tight">
                Más que una estancia,{" "}
                <span className="text-[#E05A47]">un hogar</span>
              </h1>
              <p className="text-stone-600 text-lg leading-relaxed">
                Descubre el confort de un departamento completamente equipado en el corazón de Tampico.
                Ideal para familias y grupos de hasta 7 personas.
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <div className="price-tag text-3xl">
                  $1,500 <span className="text-lg font-normal text-stone-500">MXN/noche</span>
                </div>
                <div className="location-badge">
                  <Users weight="duotone" className="w-4 h-4" />
                  Hasta 7 personas
                </div>
              </div>
              <Link to="/reservar">
                <Button
                  size="lg"
                  className="bg-[#E05A47] hover:bg-[#C84938] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-[#E05A47]/25 active:scale-95 transition-transform"
                  data-testid="hero-reserve-btn"
                >
                  Reservar ahora
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>

            {/* Bento Gallery */}
            <motion.div
              className="lg:col-span-7"
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                <motion.div
                  variants={fadeInUp}
                  className="col-span-2 row-span-2 rounded-3xl overflow-hidden cursor-pointer"
                  onClick={() => { setSelectedImage(0); setGalleryOpen(true); }}
                >
                  <img
                    src={IMAGES.sala.url}
                    alt={IMAGES.sala.alt}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    data-testid="gallery-main-image"
                  />
                </motion.div>
                <motion.div
                  variants={fadeInUp}
                  className="rounded-2xl overflow-hidden cursor-pointer"
                  onClick={() => { setSelectedImage(1); setGalleryOpen(true); }}
                >
                  <img
                    src={IMAGES.comedor.url}
                    alt={IMAGES.comedor.alt}
                    className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </motion.div>
                <motion.div
                  variants={fadeInUp}
                  className="rounded-2xl overflow-hidden cursor-pointer"
                  onClick={() => { setSelectedImage(4); setGalleryOpen(true); }}
                >
                  <img
                    src={IMAGES.recamara1.url}
                    alt={IMAGES.recamara1.alt}
                    className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="space-y-6">
              <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#E05A47]">
                Sobre el departamento
              </span>
              <h2 className="font-['Plus_Jakarta_Sans'] text-3xl md:text-4xl font-bold text-stone-900">
                Un espacio diseñado para tu descanso
              </h2>
              <p className="text-stone-600 leading-relaxed">
                Nuestro departamento ofrece el equilibrio perfecto entre comodidad y funcionalidad.
                Con 2 recámaras amplias, 2 baños completos, una sala acogedora y una cocina totalmente equipada,
                tendrás todo lo que necesitas para sentirte en casa.
              </p>
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2 bg-[#F5F0E6] px-4 py-2 rounded-full">
                  <Bed weight="duotone" className="w-5 h-5 text-[#8C6348]" />
                  <span className="text-sm font-medium text-stone-700">2 Recámaras</span>
                </div>
                <div className="flex items-center gap-2 bg-[#F5F0E6] px-4 py-2 rounded-full">
                  <Bathtub weight="duotone" className="w-5 h-5 text-[#8C6348]" />
                  <span className="text-sm font-medium text-stone-700">2 Baños</span>
                </div>
                <div className="flex items-center gap-2 bg-[#F5F0E6] px-4 py-2 rounded-full">
                  <Television weight="duotone" className="w-5 h-5 text-[#8C6348]" />
                  <span className="text-sm font-medium text-stone-700">Sala con TV</span>
                </div>
                <div className="flex items-center gap-2 bg-[#F5F0E6] px-4 py-2 rounded-full">
                  <Fire weight="duotone" className="w-5 h-5 text-[#8C6348]" />
                  <span className="text-sm font-medium text-stone-700">Boiler</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 pt-4">
                <div className="rule-badge info">
                  <Clock className="w-4 h-4" />
                  Estancia mínima 1 noche
                </div>
                <div className="rule-badge warning">
                  <PawPrint className="w-4 h-4" />
                  No se permiten mascotas
                </div>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {amenities.map((amenity, index) => (
                  <div key={index} className="amenity-card">
                    <div className="w-12 h-12 rounded-xl bg-[#F5F0E6] flex items-center justify-center flex-shrink-0">
                      <amenity.icon weight="duotone" className="w-6 h-6 text-[#E05A47]" />
                    </div>
                    <div>
                      <p className="font-semibold text-stone-800 text-sm">{amenity.label}</p>
                      <p className="text-xs text-stone-500">{amenity.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Policies & Rules Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="policies-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#E05A47]">
              Lo que debes saber
            </span>
            <h2 className="font-['Plus_Jakarta_Sans'] text-3xl md:text-4xl font-bold text-stone-900 mt-4">
              Políticas y Reglas
            </h2>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {/* Cancellation Policy */}
            <motion.div
              variants={fadeInUp}
              className="bg-gradient-to-br from-stone-50 to-stone-100 rounded-3xl p-8 border border-stone-200"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#E05A47]/10 rounded-xl flex items-center justify-center">
                  <CalendarX weight="duotone" className="w-6 h-6 text-[#E05A47]" />
                </div>
                <h3 className="font-['Plus_Jakarta_Sans'] text-xl font-bold text-stone-900">
                  Políticas de Cancelación
                </h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle weight="fill" className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <p className="text-stone-600">
                    <span className="font-semibold text-stone-800">Cancelación gratuita</span> durante las primeras 24 horas después de reservar.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <Warning weight="fill" className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-stone-600">
                    Pasado ese plazo, la reservación <span className="font-semibold text-stone-800">no será reembolsable</span>.
                  </p>
                </div>
                <p className="text-sm text-stone-500 mt-4 pt-4 border-t border-stone-200">
                  Consulta la política completa con el anfitrión para obtener más información.
                </p>
              </div>
            </motion.div>

            {/* House Rules */}
            <motion.div
              variants={fadeInUp}
              className="bg-gradient-to-br from-stone-50 to-stone-100 rounded-3xl p-8 border border-stone-200"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#E05A47]/10 rounded-xl flex items-center justify-center">
                  <House weight="duotone" className="w-6 h-6 text-[#E05A47]" />
                </div>
                <h3 className="font-['Plus_Jakarta_Sans'] text-xl font-bold text-stone-900">
                  Reglas de la Casa
                </h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <SignIn weight="duotone" className="w-5 h-5 text-stone-500" />
                  <p className="text-stone-600">
                    <span className="font-semibold text-stone-800">Llegada:</span> 15:00 hrs - 01:00 hrs
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <SignOut weight="duotone" className="w-5 h-5 text-stone-500" />
                  <p className="text-stone-600">
                    <span className="font-semibold text-stone-800">Salida:</span> Antes de las 12:00 hrs
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Users weight="duotone" className="w-5 h-5 text-stone-500" />
                  <p className="text-stone-600">
                    <span className="font-semibold text-stone-800">Máximo:</span> 6 huéspedes
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <Users weight="duotone" className="w-5 h-5 text-stone-500 mt-0.5" />
                  <p className="text-stone-600">
                    <span className="font-semibold text-stone-800">Huésped adicional:</span> $200 MXN por persona extra
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <PawPrint weight="duotone" className="w-5 h-5 text-stone-500" />
                  <p className="text-stone-600">
                    <span className="font-semibold text-stone-800">Mascotas:</span> No se permiten
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-20 md:py-28 bg-[#F5F0E6]" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#E05A47]">
              Ubicación
            </span>
            <h2 className="font-['Plus_Jakarta_Sans'] text-3xl md:text-4xl font-bold text-stone-900 mt-4">
              En el corazón de Tampico
            </h2>
            <p className="text-stone-600 mt-4 max-w-2xl mx-auto">
              Av. B, Edificio 110, Depto. A, Col. Infonavit Universidad
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {[
              { time: "15 min", place: "Centro de Tampico" },
              { time: "5 min", place: "Altama" },
              { time: "15 min", place: "Playa Miramar" },
              { time: "10 min", place: "Laguna del Carpintero" },
            ].map((location, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-2xl p-6 text-center border border-stone-200"
              >
                <MapPin weight="duotone" className="w-8 h-8 text-[#E05A47] mx-auto mb-3" />
                <p className="text-2xl font-bold text-stone-900">{location.time}</p>
                <p className="text-sm text-stone-600 mt-1">{location.place}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Location Images Grid */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {/* Altama */}
            <motion.div variants={fadeInUp} className="rounded-2xl overflow-hidden relative group">
              <img
                src="https://customer-assets.emergentagent.com/job_estancia-tampico/artifacts/xjho6zsz_altma.jpg"
                alt="Plaza Altama"
                className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                <p className="text-white font-semibold">Altama</p>
                <p className="text-white/80 text-sm">A 5 minutos</p>
              </div>
            </motion.div>

            {/* Playa Miramar */}
            <motion.div variants={fadeInUp} className="rounded-2xl overflow-hidden relative group">
              <img
                src={IMAGES.beach.url}
                alt="Playa Miramar"
                className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                <p className="text-white font-semibold">Playa Miramar</p>
                <p className="text-white/80 text-sm">A 15 minutos</p>
              </div>
            </motion.div>

            {/* Laguna del Carpintero */}
            <motion.div variants={fadeInUp} className="rounded-2xl overflow-hidden relative group">
              <img
                src="https://customer-assets.emergentagent.com/job_estancia-tampico/artifacts/ktfw5yo7_metro.jpg"
                alt="Laguna del Carpintero"
                className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                <p className="text-white font-semibold">Laguna del Carpintero</p>
                <p className="text-white/80 text-sm">A 10 minutos</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Restaurants Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="restaurants-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#E05A47]">
              Gastronomía
            </span>
            <h2 className="font-['Plus_Jakarta_Sans'] text-3xl md:text-4xl font-bold text-stone-900 mt-4">
              Restaurantes cercanos
            </h2>
            <p className="text-stone-600 mt-4 max-w-2xl mx-auto">
              Disfruta de una excelente ubicación con acceso a algunos de los mejores restaurantes de Tampico.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {restaurants.map((restaurant, index) => (
              <motion.div 
                key={index} 
                variants={fadeInUp} 
                className="bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-sm hover:shadow-lg transition-shadow group"
              >
                <div className="h-32 overflow-hidden bg-stone-100">
                  <img
                    src={restaurant.image}
                    alt={restaurant.name}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-stone-800 text-sm">{restaurant.name}</h3>
                  <p className="text-xs text-stone-500 mt-1">{restaurant.type}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <p className="text-center text-stone-500 mt-8">
            Todos se encuentran a pocos minutos del departamento.
          </p>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 md:py-28 bg-[#FDFBF7]" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#E05A47]">
              Galería
            </span>
            <h2 className="font-['Plus_Jakarta_Sans'] text-3xl md:text-4xl font-bold text-stone-900 mt-4">
              Conoce cada espacio
            </h2>
          </motion.div>

          <motion.div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {galleryImages.map((img, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="rounded-2xl overflow-hidden cursor-pointer group relative aspect-square"
                onClick={() => { setSelectedImage(index); setGalleryOpen(true); }}
              >
                <img
                  src={img.url}
                  alt={img.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  data-testid={`gallery-image-${index}`}
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <p className="text-white font-medium text-sm">{img.label}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Basketball Court Section */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-[#1a365d] to-[#2d4a7c] relative overflow-hidden" data-testid="basketball-section">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 border-4 border-white rounded-full"></div>
          <div className="absolute bottom-10 right-10 w-60 h-60 border-4 border-white rounded-full"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <motion.div
              className="text-white"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <span className="inline-flex items-center gap-2 uppercase tracking-[0.2em] text-xs font-semibold text-orange-400 bg-white/10 px-4 py-2 rounded-full mb-6">
                <Basketball weight="fill" className="w-4 h-4" />
                Área recreativa
              </span>
              <h2 className="font-['Plus_Jakarta_Sans'] text-3xl md:text-5xl font-bold mb-6 leading-tight">
                ¿Listo para un partido?
              </h2>
              <p className="text-white/80 text-lg leading-relaxed mb-8">
                El fraccionamiento cuenta con una <strong className="text-white">cancha de basketball</strong> disponible para todos nuestros huéspedes. 
                Perfecta para mantenerte activo, jugar con amigos o pasar un rato divertido con la familia.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 border border-white/20">
                  <p className="text-orange-400 font-bold text-lg">Gratis</p>
                  <p className="text-white/70 text-sm">Para huéspedes</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 border border-white/20">
                  <p className="text-orange-400 font-bold text-lg">Todo el día</p>
                  <p className="text-white/70 text-sm">Disponibilidad</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 border border-white/20">
                  <p className="text-orange-400 font-bold text-lg">Familiar</p>
                  <p className="text-white/70 text-sm">Para todas las edades</p>
                </div>
              </div>
            </motion.div>

            {/* Image */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="relative"
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-orange-400 to-yellow-400 rounded-3xl opacity-20 blur-xl"></div>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white/20">
                <img
                  src="https://customer-assets.emergentagent.com/job_estancia-tampico/artifacts/n1opiohk_WhatsApp%20Image%202026-03-24%20at%209.52.04%20AM.jpeg"
                  alt="Cancha de basketball del fraccionamiento"
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                  <p className="text-white font-semibold text-lg">Cancha de basketball</p>
                  <p className="text-white/80 text-sm">Dentro del fraccionamiento</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#E05A47]">
              Testimonios
            </span>
            <h2 className="font-['Plus_Jakarta_Sans'] text-3xl md:text-4xl font-bold text-stone-900 mt-4">
              Lo que dicen nuestros huéspedes
            </h2>
            <p className="text-stone-600 mt-4">
              Más de 50 huéspedes satisfechos
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {allTestimonials.slice(0, 3).map((testimonial, index) => (
              <motion.div key={testimonial.id || index} variants={fadeInUp} className="testimonial-card">
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-stone-800">{testimonial.name}</p>
                    <p className="text-sm text-stone-500">{testimonial.time}</p>
                  </div>
                </div>
                <div className="star-rating mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} weight="fill" className="w-5 h-5" />
                  ))}
                </div>
                <p className="text-stone-600 leading-relaxed">{testimonial.comment}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Additional testimonials in smaller cards */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {allTestimonials.slice(3, 5).map((testimonial, index) => (
              <motion.div
                key={testimonial.id || index}
                variants={fadeInUp}
                className="flex items-start gap-4 bg-[#F5F0E6] rounded-2xl p-5"
              >
                <img
                  src={testimonial.avatar}
                  alt={testimonial.name}
                  className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                />
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-stone-800 text-sm">{testimonial.name}</p>
                    <span className="text-xs text-stone-500">{testimonial.time}</span>
                  </div>
                  <div className="star-rating mb-2">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} weight="fill" className="w-4 h-4" />
                    ))}
                  </div>
                  <p className="text-stone-600 text-sm">{testimonial.comment}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Add Review Button */}
          <motion.div
            className="text-center mt-10"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <Button
              onClick={() => setReviewFormOpen(true)}
              variant="outline"
              size="lg"
              className="rounded-full px-8 py-6 border-2 border-[#E05A47] text-[#E05A47] hover:bg-[#E05A47] hover:text-white transition-all font-semibold"
              data-testid="add-review-btn"
            >
              <PencilSimple className="w-5 h-5 mr-2" />
              Deja tu reseña
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Review Form Modal */}
      <ReviewForm
        isOpen={reviewFormOpen}
        onClose={() => setReviewFormOpen(false)}
        onSuccess={fetchReviews}
      />

      {/* CTA Section */}
      <section className="py-20 cta-gradient" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="font-['Plus_Jakarta_Sans'] text-3xl md:text-4xl font-bold text-white mb-4">
              Reserva hoy y vive Tampico como en casa
            </h2>
            <p className="text-white/90 text-lg mb-8">
              Alta demanda - pocas fechas disponibles
            </p>
            <Link to="/reservar">
              <Button
                size="lg"
                className="bg-white text-[#E05A47] hover:bg-stone-100 rounded-full px-10 py-6 text-lg font-semibold shadow-xl active:scale-95 transition-transform"
                data-testid="cta-reserve-btn"
              >
                Ver disponibilidad
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-stone-900 text-stone-400" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <House weight="duotone" className="w-6 h-6 text-[#E05A47]" />
                <span className="font-['Plus_Jakarta_Sans'] font-bold text-lg text-white">
                  Tu Hogar en Tampico
                </span>
              </div>
              <p className="text-sm leading-relaxed">
                Tu espacio de descanso en el corazón de Tampico, México.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Dirección</h4>
              <p className="text-sm flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                Av. B, Edificio 110, Depto. A,<br />
                Col. Infonavit Universidad,<br />
                Tampico, México
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Contacto</h4>
              <div className="space-y-2 text-sm">
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-[#25D366] transition-colors"
                  data-testid="footer-whatsapp-link"
                >
                  <WhatsappLogo className="w-4 h-4" />
                  +52 833 303 9700
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-stone-800 mt-8 pt-8 text-center text-sm">
            <p>© 2026 Tu Hogar en Tampico. Todos los derechos reservados.</p>
            <button
              onClick={() => setAdminModalOpen(true)}
              className="mt-4 text-stone-500 hover:text-stone-300 transition-colors text-xs flex items-center gap-1.5 mx-auto"
              data-testid="admin-access-btn"
            >
              <Lock className="w-3 h-3" />
              Acceso Propietario
            </button>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a
        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hola, me interesa rentar el departamento. ¿Está disponible?")}`}
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
        data-testid="floating-whatsapp-btn"
      >
        <WhatsappLogo weight="fill" className="w-8 h-8 text-white" />
      </a>

      {/* Gallery Lightbox */}
      {galleryOpen && (
        <div
          className="gallery-overlay"
          onClick={() => setGalleryOpen(false)}
          data-testid="gallery-lightbox"
        >
          <button
            className="absolute top-6 right-6 text-white hover:text-stone-300 transition-colors"
            onClick={() => setGalleryOpen(false)}
            data-testid="close-gallery-btn"
          >
            <X className="w-8 h-8" />
          </button>
          <div className="text-center">
            <img
              src={galleryImages[selectedImage].url}
              alt={galleryImages[selectedImage].alt}
              className="max-w-[90vw] max-h-[80vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="text-white mt-4 text-lg font-medium">{galleryImages[selectedImage].label}</p>
          </div>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
            {galleryImages.map((_, index) => (
              <button
                key={index}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === selectedImage ? "bg-white" : "bg-white/40"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImage(index);
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Admin Access Modal */}
      <AnimatePresence>
        {adminModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
            onClick={() => {
              setAdminModalOpen(false);
              setAdminPassword("");
              setAdminError("");
            }}
            data-testid="admin-modal-overlay"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="admin-modal"
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Key weight="duotone" className="w-8 h-8 text-stone-600" />
                </div>
                <h3 className="text-xl font-bold text-stone-800">Acceso Propietario</h3>
                <p className="text-sm text-stone-500 mt-1">Ingresa tu contraseña para continuar</p>
              </div>

              <form onSubmit={handleAdminAccess} className="space-y-4">
                <div>
                  <input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => {
                      setAdminPassword(e.target.value);
                      setAdminError("");
                    }}
                    placeholder="Contraseña"
                    className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E05A47] focus:border-transparent text-center text-lg tracking-widest"
                    autoFocus
                    data-testid="admin-password-input"
                  />
                  {adminError && (
                    <p className="text-red-500 text-sm mt-2 text-center" data-testid="admin-error">
                      {adminError}
                    </p>
                  )}
                </div>
                
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setAdminModalOpen(false);
                      setAdminPassword("");
                      setAdminError("");
                    }}
                    className="flex-1 py-3 border border-stone-200 text-stone-600 rounded-xl hover:bg-stone-50 transition-colors font-medium"
                    data-testid="admin-cancel-btn"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3 bg-[#E05A47] text-white rounded-xl hover:bg-[#C84938] transition-colors font-medium flex items-center justify-center gap-2"
                    data-testid="admin-submit-btn"
                  >
                    <Lock className="w-4 h-4" />
                    Entrar
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
